import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { isContentSettled, useLiveTick } from '@/lib/liveContent';
import { getReach, REACH_DEMO_HREF, REACH_PRICING_HREF, REACH_ROUTE } from '@/data/reach';
import { trackEvent } from '@/lib/analytics';
import { ReachHero } from '@/components/site/reach/ReachHero';
import { ReachChallenge } from '@/components/site/reach/ReachChallenge';
import { ReachPipeline } from '@/components/site/reach/ReachPipeline';
import { ReachCapabilities } from '@/components/site/reach/ReachCapabilities';
import { ReachDashboards } from '@/components/site/reach/ReachDashboards';
import { ReachPlanner } from '@/components/site/reach/ReachPlanner';
import { ReachMylo } from '@/components/site/reach/ReachMylo';
import { ReachReports } from '@/components/site/reach/ReachReports';
import { ReachImplementation } from '@/components/site/reach/ReachImplementation';
import { ReachAudiences } from '@/components/site/reach/ReachAudiences';
import { ReachPricing } from '@/components/site/reach/ReachPricing';
import { ReachFaq } from '@/components/site/reach/ReachFaq';
import { ReachRelated } from '@/components/site/reach/ReachRelated';
import { CtaBand } from '@/components/site/CtaBand';

// Public Amden Reach product-marketing page. All content reads from the
// CMS-backed listing record (cms_listings / amden-reach); the Reach
// application itself is separate and untouched.
export default function ReachPage() {
  useLiveTick();
  const listingsSettled = isContentSettled('listings');
  const listing = getReach();
  const sp = listing?.systemPage;
  const ready = listing && listing.status === 'published' && sp;

  useSeo({
    title: listing?.seo?.title || 'Amden Reach | Sales CRM & Pipeline Management Platform',
    description:
      listing?.seo?.description ||
      'Bring customers, pipeline, follow-ups, referrals and sales reporting together in one practical CRM — with the Ask Mylo AI assistant grounded in your data.',
    canonical: listing?.seo?.canonical || `https://amden.ie${REACH_ROUTE}`,
    ogImage: listing?.seo?.ogImage,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://amden.ie/' },
            { '@type': 'ListItem', position: 2, name: 'Amden Reach', item: `https://amden.ie${REACH_ROUTE}` },
          ],
        },
        // Verified facts only — no offers, ratings or invented metrics.
        {
          '@type': 'SoftwareApplication',
          name: 'Amden Reach',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: sp?.strapline,
          url: `https://amden.ie${REACH_ROUTE}`,
          provider: { '@type': 'Organization', name: 'Amden Group', url: 'https://amden.ie' },
        },
      ],
    },
  });

  useEffect(() => {
    if (ready) trackEvent('reach_page_viewed');
  }, [ready]);

  if (!ready) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center" data-testid="reach-loading">
        {!listingsSettled ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
            <span className="sr-only">Loading Amden Reach</span>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Amden Reach</h1>
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              This page is currently being updated. To learn about Amden Reach in the meantime, contact the Amden team.
            </p>
            <Link to="/contact" className="mt-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
              Contact Amden Group
            </Link>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <ReachHero sp={sp} />
      <ReachChallenge sp={sp} />
      <ReachPipeline sp={sp} />
      <ReachCapabilities sp={sp} />
      <ReachDashboards sp={sp} />
      <ReachPlanner sp={sp} />
      <ReachMylo sp={sp} />
      <ReachReports sp={sp} />
      <ReachImplementation sp={sp} />
      <ReachAudiences sp={sp} />
      <ReachPricing sp={sp} listing={listing} />
      <ReachFaq />
      <ReachRelated sp={sp} />
      <CtaBand
        index="14"
        eyebrow="Amden Reach"
        heading="Ready to run a clearer sales operation?"
        copy="See how Amden Reach can keep every lead, follow-up and opportunity visible — and give your team a plan for every day."
        primaryLabel="Book a Reach Demonstration"
        primaryHref={REACH_DEMO_HREF}
        secondaryLabel="Request Pricing"
        secondaryHref={REACH_PRICING_HREF}
      />
    </>
  );
}
